import React from 'react';
import styled from 'styled-components';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  accordionSummaryClassNames,
  ChevronDownIcon,
} from '@moonsifttech/design-system';

import TopMenu from 'src/mvp22/menu-components/TopMenu';
import R from 'src/routes';
import { Link } from 'src/components/core/Link';
import { CreatorFaqProps } from './CreatorFaq.types';

const UnstyledCreatorFaq: React.FC<CreatorFaqProps> = ({
  className,
  style,
}) => {
  return (
    <div className={className} style={style}>
      <TopMenu className="CreatorFaq-topMenu" />
      <div className="CreatorFaq-innerContainer">
        <Typography
          className="CreatorFaq-heading"
          variant="primary.b34"
          component="h1"
        >
          Support & FAQ
        </Typography>

        <Accordion>
          <AccordionSummary
            id="commissions-header"
            aria-controls="commissions-content"
            expandIcon={<ChevronDownIcon size="small" />}
          >
            <Typography variant="primary.m16" component="h2">
              Commission rates by region
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="primary.r16" component="p">
              On the <Link to={R.AFFILIATE_BRANDS}>Affiliate brands list</Link>{' '}
              you will see a list of countries under each brand.
            </Typography>
            <Typography variant="primary.r16" component="p">
              Brands set their commissions by country. This does not relate to
              the country in which you are based, but where the person buying
              the product is based i.e. where the transaction takes place.
            </Typography>
            <Typography variant="primary.r16" component="p">
              In short, if someone buys from your collection in one of these
              countries you are eligible to earn commission.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary
            id="start-earning-header"
            aria-controls="start-earning-content"
            expandIcon={<ChevronDownIcon size="small" />}
          >
            <Typography variant="primary.m16" component="h2">
              What do I need to do for items in my collection to be eligible for
              commission?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="primary.r16" component="p">
              Nothing.
              <br />
              When you add a product to Moonsift from one of our{' '}
              <Link to={R.AFFILIATE_BRANDS}>Affiliate brands</Link>, you are
              automatically be set up to earn commission when people click
              through and buy.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary
            id="payment-header"
            aria-controls="payment-content"
            expandIcon={<ChevronDownIcon size="small" />}
          >
            <Typography variant="primary.m16" component="h2">
              How will I receive payment?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="primary.r16" component="p">
              Payments are sent to you via PayPal using your email address. You
              will be asked to confirm which email address Moonsift should send
              payment to after clicking the 'Request payout' button.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary
            id="pending-earnings-header"
            aria-controls="pending-earnings-content"
            expandIcon={<ChevronDownIcon size="small" />}
          >
            <Typography variant="primary.m16" component="h2">
              Why are earnings 'Open'?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="primary.r16" component="p">
              Most Brands do not pay commissions for at least 92 days after the
              transaction date. For many , this period can be longer. This is
              largely so they can account for return policies.
            </Typography>
            <Typography variant="primary.r16" component="p">
              Once we receive the commission from the brand, we move your
              earnings from 'Open' to 'Due'.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary
            id="due-earnings-header"
            aria-controls="due-earnings-content"
            expandIcon={<ChevronDownIcon size="small" />}
          >
            <Typography variant="primary.m16" component="h2">
              When will I be paid my 'Due' earnings?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="primary.r16" component="p">
              Due earnings are available for payout when the amount reaches £50
              or more. When you request a payout, it will be sent via PayPal in
              the first week of the upcoming month.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion className="CreatorFaq-lastAccordion">
          <AccordionSummary
            id="not-attributed-header"
            aria-controls="not-attributed-content"
            expandIcon={<ChevronDownIcon size="small" />}
          >
            <Typography variant="primary.m16" component="h2">
              Why haven't I been attributed with a sale?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="primary.r16" component="p">
              Please note that it can take up to 7 days for a sale appear as
              'Open' in your My Earnings page. The best day to check is Monday.
            </Typography>
            <Typography variant="primary.r16" component="p">
              There are a several reasons why a retailer may not always credit
              you with a sale:
            </Typography>
            <Typography variant="primary.r16" component="p">
              1. Some retailers do not pay commission on all products, excluding
              specific product categories or brands from their commission
              programme.
            </Typography>
            <Typography variant="primary.r16" component="p">
              2. Another marketing channel (e.g. a Facebook Ad or email
              newsletter) got the last click. In the time between clicking
              through from your Collection and buying the item, the customer may
              have clicked through to the retailer's site via another link. If
              so you may not have been their last click.
            </Typography>
            <Typography variant="primary.r16" component="p">
              3. The user might be based in a country not supported by the
              retailer's commission program. Brands do not pay commission on
              transactions in every region. Check the{' '}
              <Link to={R.AFFILIATE_BRANDS}>Affiliate brands</Link> list for
              more info.
            </Typography>
            <Typography variant="primary.r16" component="p">
              4. The user may be using an ad-blocking software that blocks
              cookies on their browser or cleared their cookies before purchase.
              Most of the time this shouldn't matter though.
            </Typography>
            <Typography variant="primary.r16" component="p">
              5. The retailer cancelled the commission because the item was
              returned or cancelled by the customer. Retailers may also cancel
              commissions if it was a duplicate purchase (e.g. if the shopper
              had previously purchased and returned the item).
            </Typography>
            <Typography variant="primary.r16" component="p">
              6. Some retailers do not pay 'self-referral' i.e. when you are
              referring yourself.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Typography variant="primary.r16" component="p">
          Go to our <Link to={R.HOW_TO_SAVE}>Help & FAQs</Link> for all other
          questions relating to using Moonsift
        </Typography>
      </div>
    </div>
  );
};

export const CreatorFaq = styled(UnstyledCreatorFaq)`
  .CreatorFaq-topMenu {
    height: 56px;
    margin-bottom: 28px;
  }

  .CreatorFaq-innerContainer {
    max-width: 328px;
    margin: 0 auto 40px;
  }

  .CreatorFaq-heading {
    margin-bottom: 28px;
  }

  .${accordionSummaryClassNames.root} {
    outline: 0;
  }

  .${accordionSummaryClassNames.content} > h2 {
    margin-bottom: 0;
  }

  .CreatorFaq-lastAccordion {
    margin-bottom: 32px;
  }
`;
